// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/template-compiler.ts
// Từ các cột của một mẫu suy ra bộ công thức cần tính, các biến nhập tay, biến thỏa thuận và lỗi cấu hình.
import { canonicalPayrollCode } from './domain';
import { extractPayrollDependencies, sortPayrollFormulas } from './formulaEvaluator';
import { getPayrollVariableSource } from './sourceRegistry';

/**
 * input.columns: [{ variableId, formula, order, required, visible, allowInput }]
 * input.variables: [{ id, code, name, kind, source, formula, active }]
 */
export function compilePayrollTemplate(input) {
    const errors = [];
    const warnings = [];
    const definitionsById = new Map(input.variables.map((variable) => [variable.id, variable]));
    const definitionsByCode = new Map(input.variables.map((variable) => [canonicalPayrollCode(variable.code), variable]));
    const selected = [];
    const selectedCodes = new Set();

    for (const column of [...input.columns].sort((left, right) => left.order - right.order)) {
        const variable = definitionsById.get(column.variableId);
        if (!variable || variable.active === false) {
            errors.push({ code: 'VARIABLE_NOT_FOUND', message: 'Mẫu chứa biến không tồn tại hoặc đã ngừng sử dụng.' });
            continue;
        }
        const code = canonicalPayrollCode(variable.code);
        if (selectedCodes.has(code)) {
            errors.push({ code: 'DUPLICATE_VARIABLE', variableCode: code, message: `Biến ${code} bị lặp trong mẫu.` });
            continue;
        }
        selectedCodes.add(code);
        const formula = column.formula?.trim() || variable.formula?.trim() || undefined;
        const source = formula ? 'CONG_THUC' : getPayrollVariableSource(code, variable.source);
        if (column.allowInput && source !== 'NHAP_KY') {
            errors.push({ code: 'INVALID_MANUAL_INPUT', variableCode: code, message: `Biến ${code} không phải nguồn nhập tại bảng lương.` });
        }
        selected.push({
            variableId: variable.id,
            code,
            name: variable.name,
            source,
            formula,
            order: column.order,
            required: column.required ?? false,
            visible: column.visible ?? true,
            allowInput: source === 'NHAP_KY' && (column.allowInput ?? true),
            dependencyOnly: false,
        });
    }

    for (const requiredCode of ['TC', 'TCHI']) {
        if (!selectedCodes.has(requiredCode)) errors.push({ code: 'MISSING_SEMANTIC_VARIABLE', variableCode: requiredCode, message: `Mẫu bắt buộc phải có biến ${requiredCode}.` });
    }

    const dependencyCodes = new Set();
    const formulas = {};
    // Công thức riêng mẫu đặt cho cột của nó phải thắng công thức mặc định của biến, kể cả khi cột đó
    // được công thức khác tham chiếu tới. Ví dụ thật: mẫu đặt TTC = ROUND(GTC * DGTC, -3) nhưng TC tham
    // chiếu TTC, nếu lấy công thức mặc định thì tiền tăng ca ra không làm tròn.
    const templateFormulaByCode = new Map(selected.flatMap((column) => (column.formula ? [[column.code, column.formula]] : [])));
    const visitDependencies = (column, path) => {
        if (!column.formula) return;
        formulas[column.code] = column.formula;
        for (const dependencyCode of extractPayrollDependencies(column.formula)) {
            const code = canonicalPayrollCode(dependencyCode);
            if (path.includes(code)) {
                errors.push({ code: 'FORMULA_CYCLE', variableCode: column.code, message: `Vòng lặp công thức: ${[...path, code].join(' -> ')}` });
                continue;
            }
            const definition = definitionsByCode.get(code);
            if (!definition || definition.active === false) {
                errors.push({ code: 'MISSING_DEPENDENCY', variableCode: column.code, message: `Công thức ${column.code} tham chiếu biến không tồn tại: ${code}.` });
                continue;
            }
            if (!selectedCodes.has(code)) dependencyCodes.add(code);
            const dependencyFormula = templateFormulaByCode.get(code) ?? definition.formula?.trim();
            if (dependencyFormula) {
                formulas[code] = dependencyFormula;
                visitDependencies({
                    variableId: definition.id,
                    code,
                    name: definition.name,
                    source: 'CONG_THUC',
                    formula: dependencyFormula,
                    order: Number.MAX_SAFE_INTEGER,
                    required: false,
                    visible: false,
                    allowInput: false,
                    dependencyOnly: true,
                }, [...path, column.code]);
            }
        }
    };
    selected.forEach((column) => visitDependencies(column, []));

    const dependencyColumns = Array.from(dependencyCodes).flatMap((code) => {
        const variable = definitionsByCode.get(code);
        if (!variable) return [];
        const formula = variable.formula?.trim() || undefined;
        return [{
            variableId: variable.id,
            code,
            name: variable.name,
            source: formula ? 'CONG_THUC' : getPayrollVariableSource(code, variable.source),
            formula,
            order: Number.MAX_SAFE_INTEGER,
            required: false,
            visible: false,
            allowInput: false,
            dependencyOnly: true,
        }];
    });
    const columns = [...selected, ...dependencyColumns];
    let evaluationOrder = [];
    if (!errors.length) {
        try {
            evaluationOrder = sortPayrollFormulas(
                formulas,
                columns.filter((column) => !column.formula).map((column) => column.code),
            );
        } catch (error) {
            errors.push({ code: 'FORMULA_INVALID', message: error instanceof Error ? error.message : 'Công thức mẫu không hợp lệ.' });
        }
    }
    const activeCodes = columns.map((column) => column.code);
    const inputCodes = columns.filter((column) => column.source === 'NHAP_KY').map((column) => column.code);
    const agreementCodes = columns.filter((column) => column.source === 'THOA_THUAN').map((column) => column.code);
    for (const column of columns.filter((item) => item.required && !item.formula && item.source === 'THOA_THUAN')) {
        warnings.push({ code: 'REQUIRED_AGREEMENT_VALUE', variableCode: column.code, message: `Biến ${column.code} phải có trong thỏa thuận nhân viên.` });
    }

    return {
        templateId: input.templateId,
        columns,
        visibleCodes: selected.filter((column) => column.visible).map((column) => column.code),
        dependencyCodes: Array.from(dependencyCodes),
        inputCodes,
        agreementCodes,
        evaluationOrder,
        formulas,
        activeCodes,
        errors,
        warnings,
    };
}
